/**
 * Muscle Mint cart utilities — shipping estimator + order-note save pill.
 * Ultrapure adoption plan item 1 (worker CARTUX).
 *
 * - Shipping estimator: server-rendered inside snippets/cart-drawer.liquid
 *   (snippets/mm-cart-utilities.liquid) for the drawer. On /cart the same
 *   markup is injected here because templates/cart.json's summary block
 *   renders through a static Horizon block (blocks/_cart-summary.liquid)
 *   this project does not own.
 * - Order note: Horizon's native settings.show_cart_note accordion
 *   (snippets/cart-summary.liquid) already persists to cart attributes via
 *   its own debounced /cart/update.js call — this file only adds a "Save"
 *   pill for visual parity with the Ultrapure reference; it does not
 *   duplicate persistence logic.
 *
 * Vanilla JS. Event delegation on `document` means newly-inserted markup
 * (drawer re-renders via the Section Rendering API, or this script's own
 * cart-page injection) never needs re-binding. A single guard flag stops
 * double init if the module is ever evaluated twice.
 */
(function () {
  if (window.__mmCartUtilitiesInit) return;
  window.__mmCartUtilitiesInit = true;

  var IE_COUNTIES = [
    'Carlow', 'Cavan', 'Clare', 'Cork', 'Donegal', 'Dublin', 'Galway',
    'Kerry', 'Kildare', 'Kilkenny', 'Laois', 'Leitrim', 'Limerick',
    'Longford', 'Louth', 'Mayo', 'Meath', 'Monaghan', 'Offaly',
    'Roscommon', 'Sligo', 'Tipperary', 'Waterford', 'Westmeath',
    'Wexford', 'Wicklow'
  ];

  var TRUCK_SVG =
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
    '<rect x="1" y="3" width="15" height="13"></rect>' +
    '<polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>' +
    '<circle cx="5.5" cy="18.5" r="2.5"></circle>' +
    '<circle cx="18.5" cy="18.5" r="2.5"></circle>' +
    '</svg>';

  var CHEVRON_SVG =
    '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
    '<polyline points="6 9 12 15 18 9"></polyline>' +
    '</svg>';

  function shopifyRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function sleep(ms) {
    return new Promise(function (resolve) {
      window.setTimeout(resolve, ms);
    });
  }

  var cartCurrency = null;
  function getCartCurrency() {
    if (cartCurrency) return Promise.resolve(cartCurrency);
    return fetch(shopifyRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        cartCurrency = cart.currency || 'GBP';
        return cartCurrency;
      })
      .catch(function () {
        return 'GBP';
      });
  }

  function formatMoney(decimalString, currency) {
    var amount = parseFloat(decimalString);
    if (isNaN(amount)) return decimalString;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP'
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2) + ' ' + (currency || 'GBP');
    }
  }

  /* ---------------------------------------------------------------------
   * Shipping estimator markup (cart-page injection only — the drawer gets
   * the identical structure server-rendered by mm-cart-utilities.liquid).
   * ------------------------------------------------------------------- */
  function buildShippingUtilityMarkup() {
    return (
      '<div class="mm-cart-utilities" data-mm-cart-utilities>' +
        '<button type="button" class="mm-cart-utility" data-mm-shipping-toggle aria-expanded="false">' +
          '<span class="mm-cart-utility__icon" aria-hidden="true">' + TRUCK_SVG + '</span>' +
          '<span class="mm-cart-utility__label">Estimate shipping</span>' +
          '<span class="mm-cart-utility__chevron" aria-hidden="true">' + CHEVRON_SVG + '</span>' +
        '</button>' +
        '<div class="mm-shipping-panel" data-mm-shipping-panel hidden>' +
          '<form class="mm-shipping-panel__form" data-mm-shipping-form novalidate>' +
            '<div class="mm-shipping-panel__row">' +
              '<label class="mm-shipping-panel__field">' +
                '<span class="mm-shipping-panel__field-label">Country</span>' +
                '<select class="mm-shipping-panel__select" name="country" data-mm-shipping-country>' +
                  '<option value="GB">United Kingdom</option>' +
                  '<option value="IE">Ireland</option>' +
                '</select>' +
              '</label>' +
              '<label class="mm-shipping-panel__field" data-mm-shipping-province-field hidden>' +
                '<span class="mm-shipping-panel__field-label">County</span>' +
                '<select class="mm-shipping-panel__select" name="province" data-mm-shipping-province></select>' +
              '</label>' +
            '</div>' +
            '<label class="mm-shipping-panel__field">' +
              '<span class="mm-shipping-panel__field-label">Postal code</span>' +
              '<input class="mm-shipping-panel__input" type="text" name="zip" autocomplete="postal-code" placeholder="e.g. BT40 1AB" data-mm-shipping-zip required>' +
            '</label>' +
            '<button type="submit" class="mm-shipping-panel__submit" data-mm-shipping-submit>Estimate shipping</button>' +
          '</form>' +
          '<div class="mm-shipping-panel__results" data-mm-shipping-results aria-live="polite"></div>' +
        '</div>' +
      '</div>'
    );
  }

  function injectCartPageUtilities() {
    var main = document.querySelector('main[data-template="cart"]');
    if (!main) return;
    var summaryInner = main.querySelector('.cart-summary__inner');
    if (!summaryInner) return;
    if (summaryInner.querySelector('[data-mm-cart-utilities]')) return;
    summaryInner.insertAdjacentHTML('afterbegin', buildShippingUtilityMarkup());
  }

  function revealDrawerUtilities() {
    document.querySelectorAll('[data-mm-cart-utilities][hidden]').forEach(function (el) {
      el.hidden = false;
    });
  }

  /* ---------------------------------------------------------------------
   * Shipping estimator behaviour (event delegation — works for both the
   * server-rendered drawer copy and the JS-injected /cart copy).
   * ------------------------------------------------------------------- */
  function populateProvinces(select) {
    select.innerHTML = '';
    IE_COUNTIES.forEach(function (county) {
      var option = document.createElement('option');
      option.value = county;
      option.textContent = county;
      select.appendChild(option);
    });
  }

  function renderRates(container, rates, currency) {
    container.innerHTML = '';
    if (!rates || !rates.length) {
      var empty = document.createElement('p');
      empty.className = 'mm-shipping-panel__empty';
      empty.textContent = 'No rates found for that address.';
      container.appendChild(empty);
      return;
    }
    var list = document.createElement('ul');
    list.className = 'mm-shipping-panel__rates';
    rates.forEach(function (rate) {
      var item = document.createElement('li');
      item.className = 'mm-shipping-panel__rate';
      var name = document.createElement('span');
      name.textContent = rate.name || rate.presentment_name || 'Shipping';
      var price = document.createElement('span');
      price.textContent = formatMoney(rate.price, currency);
      item.appendChild(name);
      item.appendChild(document.createTextNode(' - '));
      item.appendChild(price);
      list.appendChild(item);
    });
    container.appendChild(list);
  }

  function renderError(container, message) {
    container.innerHTML = '';
    var p = document.createElement('p');
    p.className = 'mm-shipping-panel__error';
    p.textContent = message;
    container.appendChild(p);
  }

  function estimateShipping(params) {
    var root = shopifyRoot();
    var qs = new URLSearchParams();
    qs.set('shipping_address[zip]', params.zip);
    qs.set('shipping_address[country]', params.country);
    if (params.province) qs.set('shipping_address[province]', params.province);

    return fetch(root.replace(/\/$/, '') + '/cart/prepare_shipping_rates.json?' + qs.toString(), {
      method: 'POST',
      headers: { Accept: 'application/json' }
    }).then(function () {
      var attempts = 0;
      function poll() {
        attempts += 1;
        return fetch(root.replace(/\/$/, '') + '/cart/async_shipping_rates.json?' + qs.toString(), {
          headers: { Accept: 'application/json' }
        }).then(function (res) {
          if (res.status === 200) {
            return res.json().then(function (data) {
              if (data && data.shipping_rates) return data.shipping_rates;
              if (attempts >= 10) return [];
              return sleep(900).then(poll);
            });
          }
          if (attempts >= 10) return [];
          return sleep(900).then(poll);
        });
      }
      return poll();
    });
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-mm-shipping-toggle]');
    if (toggle) {
      var panel = toggle.parentElement.querySelector('[data-mm-shipping-panel]');
      if (!panel) return;
      var open = panel.hidden;
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      return;
    }

    var saveBtn = event.target.closest('[data-mm-note-save]');
    if (saveBtn) {
      var wrap = saveBtn.closest('.cart-note__inner');
      var textarea = wrap && wrap.querySelector('textarea.cart-note__instructions');
      if (!textarea) return;
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
      var status = wrap.querySelector('[data-mm-note-status]');
      if (status) {
        status.textContent = 'Saved';
        window.clearTimeout(status._mmTimer);
        status._mmTimer = window.setTimeout(function () {
          status.textContent = '';
        }, 2000);
      }
    }
  });

  document.addEventListener('change', function (event) {
    var countrySelect = event.target.closest('[data-mm-shipping-country]');
    if (!countrySelect) return;
    var form = countrySelect.closest('[data-mm-shipping-form]');
    var provinceField = form.querySelector('[data-mm-shipping-province-field]');
    var provinceSelect = form.querySelector('[data-mm-shipping-province]');
    if (countrySelect.value === 'IE') {
      populateProvinces(provinceSelect);
      provinceField.hidden = false;
    } else {
      provinceSelect.innerHTML = '';
      provinceField.hidden = true;
    }
  });

  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-mm-shipping-form]');
    if (!form) return;
    event.preventDefault();

    var zipInput = form.querySelector('[data-mm-shipping-zip]');
    var countrySelect = form.querySelector('[data-mm-shipping-country]');
    var provinceSelect = form.querySelector('[data-mm-shipping-province]');
    var submitBtn = form.querySelector('[data-mm-shipping-submit]');
    var results = form.parentElement.querySelector('[data-mm-shipping-results]');
    if (!zipInput || !zipInput.value.trim()) {
      zipInput && zipInput.focus();
      return;
    }

    submitBtn.disabled = true;
    submitBtn.textContent = 'Estimating…';
    results.innerHTML = '';

    var params = {
      zip: zipInput.value.trim(),
      country: countrySelect.value,
      province: countrySelect.value === 'IE' ? provinceSelect.value : ''
    };

    Promise.all([estimateShipping(params), getCartCurrency()])
      .then(function (res) {
        renderRates(results, res[0], res[1]);
      })
      .catch(function () {
        renderError(results, "Couldn't estimate shipping right now. Please try again.");
      })
      .finally(function () {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Estimate shipping';
      });
  });

  /* ---------------------------------------------------------------------
   * Order-note save pill — appended once per cart-note textarea. Reuses
   * Horizon's native debounced input handler; does not persist anything
   * itself.
   * ------------------------------------------------------------------- */
  function enhanceCartNotes(root) {
    var scope = root || document;
    var inners = scope.querySelectorAll('.cart-note__inner:not([data-mm-note-enhanced])');
    inners.forEach(function (inner) {
      var textarea = inner.querySelector('textarea.cart-note__instructions');
      if (!textarea) return;
      inner.setAttribute('data-mm-note-enhanced', 'true');

      var actions = document.createElement('div');
      actions.className = 'mm-cart-note__actions';

      var saveBtn = document.createElement('button');
      saveBtn.type = 'button';
      saveBtn.className = 'mm-cart-note__save';
      saveBtn.setAttribute('data-mm-note-save', '');
      saveBtn.textContent = 'Save note';

      var status = document.createElement('span');
      status.className = 'mm-cart-note__status';
      status.setAttribute('data-mm-note-status', '');
      status.setAttribute('aria-live', 'polite');

      actions.appendChild(saveBtn);
      actions.appendChild(status);
      inner.appendChild(actions);
    });
  }

  /* ---------------------------------------------------------------------
   * Boot + self-healing re-sync.
   *
   * Horizon's Section Rendering API re-renders and morphs cart markup on
   * every cart mutation (add/remove/qty/note/discount). That can wipe
   * client-inserted nodes (the /cart injected utility row, the note save
   * pill). A single debounced MutationObserver keeps everything in sync
   * without tracking every possible cart event by name.
   * ------------------------------------------------------------------- */
  function sync() {
    injectCartPageUtilities();
    revealDrawerUtilities();
    enhanceCartNotes(document);
  }

  var syncTimer = null;
  function scheduleSync() {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(sync, 80);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }

  document.addEventListener('shopify:section:load', scheduleSync);

  if (window.MutationObserver) {
    var observer = new MutationObserver(scheduleSync);
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
